import { LoanStatus, type Loan } from 'app/types/loan';
import * as Calendar from 'expo-calendar/legacy';
import type { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { getCalendars } from 'expo-localization';
import { PermissionStatus } from 'expo';
import { Event, track } from './analytics';
import type { User } from 'app/types/user';
import { isLender } from './user';
import { APP_SCHEME } from 'app/../app.config.json';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { addDays, subDays, differenceInCalendarDays } from 'app/lib/date';
import { recordError } from 'app/lib/monitoring';
import { http } from 'app/lib/axios';

const CALENDAR_NAME = 'partage-club';
const CALENDAR_OWNER = 'Partage Club';
const CALENDAR_TITLE = `${CALENDAR_OWNER} Rendez-vous`;
const TIMEZONE = getCalendars()[0].timeZone ?? 'America/Montreal';

async function createCalendar(): Promise<string> {
  const source =
    Platform.OS === 'ios'
      ? { name: CALENDAR_OWNER, type: Calendar.SourceType.LOCAL }
      : { isLocalAccount: true, name: CALENDAR_OWNER, type: 'LOCAL' };

  return Calendar.createCalendarAsync({
    title: CALENDAR_TITLE,
    color: '#C0EBDF',
    entityType: Calendar.EntityTypes.EVENT,
    source,
    name: CALENDAR_NAME,
    ownerAccount: CALENDAR_OWNER,
    accessLevel: Calendar.CalendarAccessLevel.OWNER,
    ...(Platform.OS === 'android' ? { isVisible: true, isSynced: true, timeZone: TIMEZONE } : {}),
  });
}

async function makeCalendarVisible(calendar: Calendar.Calendar): Promise<string> {
  if (Platform.OS === 'android' && (!calendar.isVisible || !calendar.isSynced)) {
    await Calendar.updateCalendarAsync(calendar.id, { isVisible: true, isSynced: true });
  }
  return calendar.id;
}

async function getOrCreateCalendarId(): Promise<string | null> {
  try {
    const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
    const partageClubCalendar = calendars.find(
      (calendar) => calendar.title === CALENDAR_TITLE && calendar.allowsModifications,
    );
    if (partageClubCalendar) {
      return await makeCalendarVisible(partageClubCalendar);
    }
    return await createCalendar();
  } catch (error) {
    recordError(error);
    try {
      const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
      const writableCalendar = calendars.find((calendar) => calendar.allowsModifications);
      return writableCalendar?.id ?? null;
    } catch (fallbackError) {
      recordError(fallbackError);
      return null;
    }
  }
}

export type AddLoanToCalendarResult = 'success' | 'permission-denied' | 'error' | 'no-loan';
export type LoanCalendarMilestone = 'end' | 'start';
type Translate = ReturnType<typeof useTranslation>['t'];

// Full-loan, individual-milestone and automatic updates share one queue. In particular,
// a manual add must see the events moved by an automatic replacement sync before writing.
let calendarQueue: Promise<unknown> = Promise.resolve();

function enqueueCalendarUpdate(update: () => Promise<AddLoanToCalendarResult>) {
  const result = calendarQueue.then(update);
  calendarQueue = result.catch(() => undefined);
  return result;
}

function getLoanCalendarEventDetails(t: ReturnType<typeof useTranslation>['t'], userId: User['id'], loan: Loan) {
  const isLenderUser = isLender(userId, loan.listing.userId, loan.listing.type);

  const neighbourName = isLenderUser ? loan.borrower?.profile?.firstName : loan.lender?.profile?.firstName;

  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const isSell = loan.listing.transactionMode === TransactionMode.sell;

  const startTitleSuffix = isSell
    ? isLenderUser
      ? 'sellItem'
      : 'buyItem'
    : isService
      ? isLenderUser
        ? 'giveService'
        : 'receiveService'
      : isDonation
        ? isLenderUser
          ? 'donateItem'
          : 'receiveDonation'
        : isLenderUser
          ? 'lendObject.start'
          : 'borrowObject.start';

  return {
    end:
      isService || isDonation || isSell
        ? null
        : {
            date: loan.end,
            title: t(`addToCalendar.event.${isLenderUser ? 'lendObject.end' : 'borrowObject.end'}`, {
              name: neighbourName,
              item: loan.listing.item.name,
            }),
          },
    notes: `${APP_SCHEME}://main/management`,
    start: {
      date: loan.start,
      title: t(`addToCalendar.event.${startTitleSuffix}`, {
        name: neighbourName,
        item: loan.listing.item.name,
      }),
    },
  };
}

function eventNotes(userId: string, loanId: string, milestone: LoanCalendarMilestone) {
  return `${APP_SCHEME}://main/management\nloan-calendar:${userId}:${loanId}:${milestone}`;
}

async function getLoanVersions(loan: Loan): Promise<Loan[]> {
  const versions = [loan];
  const seen = new Set([loan.id]);
  let previousId = loan.status === LoanStatus.Accepted ? loan.isUpdateFor : undefined;
  while (previousId) {
    if (seen.has(previousId)) throw new Error('Cyclic calendar loan replacement');
    // A stalled history request must not block subsequent calendar actions indefinitely.
    const { data: previous } = await http.get<Loan>(`/loans/${previousId}`, { timeout: 10_000 });
    if (
      previous.id !== previousId ||
      previous.listingId !== loan.listingId ||
      previous.borrowerId !== loan.borrowerId ||
      previous.lenderId !== loan.lenderId
    ) {
      throw new Error('Invalid calendar loan replacement');
    }
    versions.push(previous);
    seen.add(previous.id);
    previousId = previous.isUpdateFor;
  }
  return versions;
}

async function findLoanEvents(
  t: Translate,
  userId: string,
  versions: Loan[],
  calendarIds: string[],
  milestone: LoanCalendarMilestone,
) {
  const matches = new Map<string, Calendar.Event>();
  for (const version of versions) {
    const details = getLoanCalendarEventDetails(t, userId, version);
    const milestoneDetails = details[milestone];
    if (!milestoneDetails) continue;
    const date = new Date(milestoneDetails.date);
    // Query each old and new day separately: replacements can be arbitrarily far apart.
    const events = await Calendar.getEventsAsync(calendarIds, subDays(date, 1), addDays(date, 2));
    for (const event of events) {
      const identified = event.notes === eventNotes(userId, version.id, milestone);
      // Older exports have only a management link. Never match by title alone, which
      // could overwrite a personal event or another loan's identified event.
      const legacy =
        event.notes === details.notes &&
        event.allDay &&
        event.title === milestoneDetails.title &&
        differenceInCalendarDays(date, new Date(event.startDate)) === 0;
      if (identified || legacy) matches.set(event.id, event);
    }
  }
  return [...matches.values()];
}

async function reconcileLoanCalendar(
  t: Translate,
  userId: string,
  loan: Loan,
  milestones: LoanCalendarMilestone[],
  createMissing: boolean,
): Promise<AddLoanToCalendarResult> {
  if (!loan.listing?.item || ![loan.borrowerId, loan.lenderId].includes(userId)) return 'no-loan';
  try {
    const { status } = createMissing
      ? await Calendar.requestCalendarPermissionsAsync()
      : await Calendar.getCalendarPermissionsAsync();
    if (createMissing) track(Event.CalendarPermissionStatus, { status });
    if (status !== PermissionStatus.GRANTED) return 'permission-denied';

    const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
    const calendarIds = calendars.filter((calendar) => calendar.allowsModifications).map((calendar) => calendar.id);
    if (!createMissing && !calendarIds.length) return 'no-loan';
    const versions = await getLoanVersions(loan);
    const details = getLoanCalendarEventDetails(t, userId, loan);
    let updated = false;
    for (const milestone of milestones) {
      const milestoneDetails = details[milestone];
      if (!milestoneDetails) continue;
      const existing = calendarIds.length ? await findLoanEvents(t, userId, versions, calendarIds, milestone) : [];
      if (!existing.length && !createMissing) continue;
      const startDate = new Date(milestoneDetails.date);
      const event = {
        title: milestoneDetails.title,
        startDate,
        // All-day events have an exclusive end date, including across DST changes.
        endDate: addDays(startDate, 1),
        timeZone: TIMEZONE,
        allDay: true,
        notes: eventNotes(userId, loan.id, milestone),
      };
      const [retained, ...duplicates] = existing;
      if (retained) {
        const calendar = calendars.find((candidate) => candidate.id === retained.calendarId);
        if (calendar) await makeCalendarVisible(calendar);
        await Calendar.updateEventAsync(retained.id, event);
        // Only remove obsolete copies after the replacement is safely written.
        for (const duplicate of duplicates) await Calendar.deleteEventAsync(duplicate.id);
      } else {
        const calendarId = await getOrCreateCalendarId();
        if (!calendarId) throw new Error('No writable loan calendar');
        if (!calendarIds.includes(calendarId)) calendarIds.push(calendarId);
        await Calendar.createEventAsync(calendarId, event);
      }
      updated = true;
    }
    if (createMissing) track(Event.LoanAddedToCalendar, { added: updated });
    return updated ? 'success' : 'no-loan';
  } catch (error: unknown) {
    recordError(error);
    if (createMissing) track(Event.LoanAddedToCalendar, { added: false });
    return 'error';
  }
}

export const addLoanToCalendar = (t: Translate, userId: User['id'], loan?: Loan): Promise<AddLoanToCalendarResult> =>
  loan
    ? enqueueCalendarUpdate(() => reconcileLoanCalendar(t, userId, loan, ['start', 'end'], true))
    : Promise.resolve('no-loan');

export const addLoanMilestoneToCalendar = (
  t: Translate,
  userId: User['id'],
  loan: Loan,
  milestone: LoanCalendarMilestone,
): Promise<AddLoanToCalendarResult> =>
  enqueueCalendarUpdate(() => reconcileLoanCalendar(t, userId, loan, [milestone], true));

/** Refresh already exported milestones without prompting or adding unsaved milestones. */
export const syncLoanCalendar = (t: Translate, userId: User['id'], loan: Loan): Promise<AddLoanToCalendarResult> =>
  loan.status === LoanStatus.Accepted && loan.isUpdateFor
    ? enqueueCalendarUpdate(() => reconcileLoanCalendar(t, userId, loan, ['start', 'end'], false))
    : Promise.resolve('no-loan');
